import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { apiClient } from '../lib/api';

export type AccountType = 'student' | 'business';

export interface UserProfile {
  name?: string | null;
  email?: string | null;
  photoUrl?: string | null;
  accountType?: AccountType;
}

export interface UserEntitlements {
  subscriptionStatus: 'none' | 'active' | 'canceled' | 'past_due' | 'unpaid' | string;
  planId?: string | null;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: unknown;
}

export interface ProfileData {
  profile: UserProfile | null;
  /**
   * `professor` é legado — não use para decisão de UI ou autorização. Olhe
   * `accountType === 'business'` (helper `useProfile().accountType`). O backend
   * ainda devolve a flag para usuários antigos do Firestore, mas nada novo a
   * escreve.
   */
  roles: { admin?: boolean; professor?: boolean };
  entitlements: UserEntitlements;
  /**
   * `FULL_ACCESS` = assinatura ativa (ou cancelada dentro do período pago).
   * `FREE` = nunca assinou ou a assinatura expirou → copy "assine".
   * `READ_ONLY` = pagamento pendente (`past_due`/`unpaid`) → copy
   * "regularize seu pagamento".
   */
  accessMode: 'FULL_ACCESS' | 'FREE' | 'READ_ONLY' | string;
  termsAcceptedAt?: string | null;
  termsVersion?: string | null;
  /**
   * Consentimento de publicidade (Guia de Cookies da ANPD). `state` é
   * `'unknown'` enquanto o titular nunca decidiu; a decisão só vale para a
   * versão em que foi tomada — use `resolveAdsConsent` (constants/ads-consent)
   * em vez de ler `state` cru.
   */
  adsConsent?: {
    state?: string | null;
    version?: string | null;
    decidedAt?: string | null;
  } | null;
}

export interface ProfileContextValue {
  data: ProfileData | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  needsOnboarding: boolean;
  accountType: AccountType;
}

/**
 * Pure helper: determina se o usuário precisa fazer onboarding.
 *
 * Retorna true SOMENTE quando já carregamos um perfil existente e ele não
 * tem `accountType`. Para usuários em loading ou sem dados, retorna false
 * — evita redirects prematuros durante o primeiro fetch.
 *
 * Usuários legados (pré-Fase 0) caem neste caminho porque o doc deles não
 * tem `profile.accountType`.
 */
export function computeNeedsOnboarding(data: ProfileData | null): boolean {
  if (!data) return false;
  if (!data.profile) return false;
  return !data.profile.accountType;
}

/**
 * Pure helper: normaliza `profile.accountType` para o tipo usado na UI.
 * Default `student` quando ausente (idêntico ao backend).
 */
export function computeAccountType(data: ProfileData | null): AccountType {
  const raw = data?.profile?.accountType;
  if (raw === 'business') return 'business';
  return 'student';
}

const ProfileContext = createContext<ProfileContextValue | undefined>(undefined);

/**
 * Provider central de profile/entitlements. Substitui as chamadas duplicadas
 * de `apiClient('/me')` espalhadas em Navbar, App, Assinatura e Conta.
 *
 * Fase 5: expõe `needsOnboarding` (baseado em `profile.accountType` ausente
 * no doc — usuários legados pré-Fase 0) e `accountType` já normalizado como
 * default `student`.
 */
export function ProfileProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading, getIdToken } = useAuth();
  const [data, setData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!user) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient('/me');
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const next = (await res.json()) as ProfileData;
      setData(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao carregar perfil');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    reload();
  }, [authLoading, reload]);

  // #2: bônus deferido (D3) só é concedido quando o token traz email_verified=true.
  // Enquanto o usuário NÃO verificou, ao voltar para a aba (visibilitychange/focus)
  // recarregamos o User do Firebase; se passou a estar verificado, forçamos refresh
  // do token (para o backend ver email_verified=true) e refazemos o fetch de /me,
  // disparando a concessão lazy do bônus. Guardado para parar após verificar (sem
  // chattiness) e limpa os listeners no cleanup.
  useEffect(() => {
    if (authLoading || !user || user.emailVerified) return;

    let cancelled = false;

    const recheckVerification = async () => {
      try {
        await user.reload();
        if (cancelled || !user.emailVerified) return;
        // Token fresco com email_verified=true → backend concede o bônus no /me.
        await getIdToken(true);
        await reload();
      } catch {
        // Falha silenciosa: não quebra a UI; nova tentativa no próximo foco.
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void recheckVerification();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', recheckVerification);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', recheckVerification);
    };
  }, [authLoading, user, getIdToken, reload]);

  const accountType: AccountType = computeAccountType(data);
  const needsOnboarding = computeNeedsOnboarding(data);

  return (
    <ProfileContext.Provider value={{ data, loading, error, reload, needsOnboarding, accountType }}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile(): ProfileContextValue {
  const ctx = useContext(ProfileContext);
  if (ctx === undefined) {
    throw new Error('useProfile must be used within ProfileProvider');
  }
  return ctx;
}
